import { AxiosResponse } from "axios";
import { ChangeEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { addUniqueItem } from "./adminHelper";
import AdminNavBar from "./components/AdminNavBar";
import apiService from "../services/api.service";

interface Chapter {
  chapter_title: string;
  chapter_number: String;
}

const BookForm = () => {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [book, setBook] = useState({
    title: "",
    author: "",
    edition: 1,
    isbn: "",
    added_chapters: chapters,
    cover_image: null as File | null,
    publish_status: "Pending Publish",
  });

  const [chapter, setChapter] = useState({
    chapter_title: "",
    chapter_number: "",
  });
  const [bookIsSubmitted, setBookIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const handleBackToSiteMaterial = () => {
    navigate("/admin/dashboard/", { state: { from: "blogposts" } });
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setBook((prevBook) => ({
        ...prevBook,
        cover_image: file,
      }));
    }
  };

  const handleBookInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setBook({
      ...book,
      [e.target.name]: e.target.value,
    });
  };

  const handleChapterInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setChapter({
      ...chapter,
      [e.target.name]: e.target.value,
    });
  };

  const addChaptersAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookIsSubmitted(true);
  };

  /**
   * Add individual chapters
   */
  const handleAddChapter = async (e: React.FormEvent) => {
    e.preventDefault();
    const compareChapters = (a: Chapter, b: Chapter) =>
      a.chapter_title === b.chapter_title ||
      a.chapter_number === b.chapter_number;

    setChapters(addUniqueItem(chapters, chapter, compareChapters));
    setChapter({ chapter_title: "", chapter_number: "" });
  };

  const handleBookSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", book.title);
    formData.append("author", book.author);
    formData.append("edition", book.edition.toString());
    formData.append("isbn", book.isbn);
    formData.append("publish_status", book.publish_status);
    chapters.forEach((chapt) => {
      formData.append(`added_chapters`, JSON.stringify(chapt));
    });

    if (book.cover_image) {
      formData.append("cover_image", book.cover_image, book.cover_image.name);
    }

    const makePostRequest = async () => {
      try {
        await apiService.post<AxiosResponse>("/books/", formData);
        alert("Book created successfully");
        setBookIsSubmitted(true);
        navigate("/admin/site-materials/books");
      } catch (error: any) {
        console.error("Error occured while creating book", error);
        if (error.response && error.response.status === 401) {
          throw new Error("Token expired");
        }
        throw error;
      }
    };

    try {
      await makePostRequest();
      // Reset the file input field (if using a controlled component)
      // const fileInput = document.getElementById(
      //   "formFileInput"
      // ) as HTMLInputElement;
      // if (fileInput) {
      //   fileInput.value = ""; // Reset the input value to clear the selected file
      // }
    } catch (error: any) {
      console.error("Error occurred while creating book", error);
      alert("Failed to create book. Please try again.");
    }
  };

  return (
    <>
      <nav className="navbar navbar-dark bg-dark">
        <AdminNavBar />
      </nav>
      <nav id="bc-nav" aria-label="breadcrumb">
        <ol id="bc-ol" className="breadcrumb">
          <li id="bc-books" className="breadcrumb-item">
            <button
              className="inactive-anchor-link"
              onClick={handleBackToSiteMaterial}
            >
              Site Materials
            </button>{" "}
            <span className="greater-than">&#x02AA2;</span>
          </li>
          <Link
            to={`/admin/site-materials/books`}
            className="breadcrumb-item inactive-anchor-link"
          >
            Books <span className="greater-than">&#x02AA2;</span>
          </Link>
          <li id="bc-active" className="breadcrumb-item active">
            create Book
          </li>
        </ol>
      </nav>
      <form id="book-form" className="mb-3" onSubmit={addChaptersAction}>
        <div className="mb-3">
          <label htmlFor="book-title" className="form-label">
            {" "}
            Title
          </label>
          <input
            className={
              bookIsSubmitted ? "form-control disable-text" : "form-control"
            }
            id="book-title"
            type="text"
            name="title"
            value={book.title}
            onChange={handleBookInputChange}
            required
            disabled={bookIsSubmitted}
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Author</label>
          <input
            className={
              bookIsSubmitted ? "form-control disable-text" : "form-control"
            }
            type="text"
            name="author"
            value={book.author}
            onChange={handleBookInputChange}
            required
            disabled={bookIsSubmitted}
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Edition </label>
          <input
            className={
              bookIsSubmitted ? "form-control disable-text" : "form-control"
            }
            type="text"
            name="edition"
            value={book.edition}
            onChange={handleBookInputChange}
            required
            disabled={bookIsSubmitted}
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> ISBN </label>
          <input
            className={
              bookIsSubmitted ? "form-control disable-text" : "form-control"
            }
            type="text"
            name="isbn"
            value={book.isbn}
            onChange={handleBookInputChange}
            required
            disabled={bookIsSubmitted}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="formFileInput" className="form-label">
            Cover Image
          </label>
          <input
            className={
              bookIsSubmitted ? "form-control disable-text" : "form-control"
            }
            id="formFileInput"
            type="file"
            name="cover_image"
            onChange={handleImageChange}
            disabled={bookIsSubmitted}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            name="publish_status"
            value={book.publish_status}
            onChange={handleBookInputChange}
            required
            disabled={bookIsSubmitted}
          >
            <option value="Pending Publish"> Pending Publish</option>
            <option value="Publish">Publish</option>
          </select>
        </div>
        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
          // onClick={handleAddChapter}
          disabled={bookIsSubmitted}
        >
          Add Chapters
        </button>
      </form>
      <hr></hr>

      <ul>
        {chapters.map((chapter, index) => (
          <li key={index}>
            {chapter.chapter_number} {chapter.chapter_title}
          </li>
        ))}
      </ul>

      <form
        className="mb-3"
        onSubmit={handleAddChapter}
        hidden={!bookIsSubmitted}
      >
        <h3> Add Chapters</h3>
        <div className="mb-3">
          <label className="form-label"> Title </label>
          <input
            className="form-control"
            type="text"
            name="chapter_title"
            value={chapter.chapter_title}
            onChange={handleChapterInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Chapter Number </label>
          <input
            className="form-control"
            type="text"
            name="chapter_number"
            value={chapter.chapter_number}
            onChange={handleChapterInputChange}
            required
          />
        </div>
        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
        >
          Add Chapter
        </button>
      </form>

      <button
        id="create-post-btn"
        className="btn error-page-btn"
        type="submit"
        onClick={handleBookSubmit}
      >
        Create Book
      </button>
    </>
  );
};

export default BookForm;
